/**
 * Where a message list should sit after its content changes.
 *
 * Three screens grew three different answers to this, and two of them were
 * wrong in the same way: an effect that called `scrollToEnd({ animated: true })`
 * on every change to the message list.
 *
 * Measured on a Galaxy S21 (build 8, SM-G998B) with `dumpsys gfxinfo`:
 *
 *   opening a thread   767 frames,  25 janky (3.26%), 99th 21ms, 0 missed vsync
 *   sending messages   1008 frames, 140 janky (13.89%), 99th 53ms, 5 missed
 *
 * Four times the jank, and it was visible as a flicker on the first three or
 * four messages specifically — which is the tell. While the thread is shorter
 * than the viewport there is nowhere to scroll, so the animation does nothing;
 * the moment the content crosses the viewport height, every send animates a
 * real jump. That is why it looked like a defect that "goes away" once a
 * conversation gets going.
 *
 * The rules, which are about the READER rather than the data:
 *
 *  - **The first paint is instant, never animated.** Opening a conversation at
 *    its newest message is a position, not a journey. Animating it is both the
 *    flicker above and a frame budget spent on something nobody asked to watch.
 *  - **Only follow a reader who is already at the bottom.** Someone who has
 *    scrolled up is reading; yanking them down because a message arrived — or
 *    worse, because THEY asked for older history and the list got longer — is
 *    precisely wrong. The group thread keyed its effect on `messages.length`,
 *    so paging in older messages would have scrolled the reader away from the
 *    thing they had just asked to see.
 *  - **Content size, not message count, is the trigger.** A count changes
 *    before the rows have laid out, so a scroll driven by it computes an offset
 *    from an incomplete content height and lands short. That is the same defect
 *    as opening a group thread at the top rather than the bottom.
 *
 * Pure on purpose: no React, no react-native import that survives type erasure,
 * so the decision is testable under node like `dashboard-cache.ts`.
 */

/**
 * How close to the bottom still counts as "following".
 *
 * Not zero: a thread sitting a few pixels off the end after a keyboard opens or
 * a row re-measures is still, to the reader, at the bottom. Roughly one message
 * bubble, so a reader who has scrolled past a whole message has clearly chosen
 * to look at something.
 */
export const FOLLOW_THRESHOLD_PX = 120;

/**
 * How long the content height must hold still before a thread counts as laid
 * out rather than still arriving.
 *
 * A virtualized list reports its height several times while rendering rows, so
 * "the content stopped growing" is the only honest signal that the thread has
 * finished opening — there is no callback for it. 120ms is comfortably longer
 * than the gap between those reports.
 *
 * Only the practice transcript still consumes this: its focus plan needs
 * "settled" to decide whether framing the end-of-session feedback may animate,
 * and to stop re-pinning a scenario card once it has laid out. The inverted
 * follow needs no settle window at all — offset 0 is correct however many
 * rows are still arriving.
 */
export const SETTLE_MS = 120;

export type ScrollGeometry = {
  /** How far the content is scrolled from its top. */
  offsetY: number;
  /** The visible height of the list. */
  viewportHeight: number;
  /** The full height of the content inside it. */
  contentHeight: number;
};

/**
 * Pixels between the bottom of the viewport and the bottom of the content.
 *
 * Clamped at zero: iOS rubber-banding reports offsets past the end, and a
 * negative distance would read as "further from the bottom than the bottom".
 */
export function distanceFromBottom(geometry: ScrollGeometry): number {
  const { offsetY, viewportHeight, contentHeight } = geometry;
  return Math.max(0, contentHeight - viewportHeight - offsetY);
}

/**
 * Is the reader close enough to the end to be following the conversation?
 *
 * Content shorter than the viewport is always following: there is nothing to
 * scroll, so the reader cannot have scrolled away.
 */
export function isFollowing(geometry: ScrollGeometry): boolean {
  if (geometry.contentHeight <= geometry.viewportHeight) return true;
  return distanceFromBottom(geometry) <= FOLLOW_THRESHOLD_PX;
}

/** What a thread should do when its content changes size. */
export type FollowAction = "jump" | "animate" | "stay";

/**
 * The whole decision, in one place.
 *
 * `settled` is the subtle one, and it is why opening a thread was INTERMITTENT
 * rather than simply wrong — reported from the device as "sometimes it loads
 * the latest, sometimes the first".
 *
 * A virtualized list does not lay out once. It reports a content height, then
 * renders more rows and reports a taller one, several times over. Treating the
 * first of those callbacks as "the thread has arrived" pins the view to the end
 * of a PARTIAL list, and where that lands depends on how many rows happened to
 * measure first — which varies with thread length, row heights, images, fonts
 * and how busy the device is. That is the race, and moving the trigger from a
 * `useEffect` to the first content-size callback would only have relocated it.
 *
 * So "settled" is a window, not an event: while the content is still growing,
 * every callback re-pins instantly to the new end. Only once the height stops
 * changing does the thread start behaving like a live conversation.
 */
export function followAction(input: {
  settled: boolean;
  following: boolean;
  hasMessages: boolean;
}): FollowAction {
  if (!input.hasMessages) return "stay";
  // A reader who has scrolled up keeps their place, whatever arrives — during
  // the opening layout as much as afterwards.
  if (!input.following) return "stay";
  // Still laying out: land on the newest message, instantly, as many times as
  // it takes for the content to stop growing.
  if (!input.settled) return "jump";
  return "animate";
}

// ------------------------------------------------------------------ practice

/**
 * Where the practice transcript should sit after its content changes.
 *
 * The same defect the one-to-one and group threads had — an animated
 * `scrollToEnd` on every change, with no idea whether the reader had scrolled
 * away — reached this screen in a milder form, because practice frames three
 * different things rather than one:
 *
 *  - a conversation → its newest bubble, ordinary chat behaviour;
 *  - nothing but the mode-intro / scenario card → the TOP of it, since there is
 *    no "latest" to follow and the bottom of an overflowing card hides the very
 *    thing the user is meant to read;
 *  - the end-of-session feedback → the top of THAT, once, because it is the
 *    last item and the chat rule would land on its buttons instead.
 *
 * That is why this screen keeps its own decision instead of the shared
 * `useThreadFollow` hook, which only knows how to chase a bottom. The two
 * pieces it was missing are borrowed rather than rewritten: `isFollowing` for
 * the reader's position, and `followAction` for the settle window. See
 * `chat-follow.ts` for the measurements that produced both.
 *
 * What was wrong here specifically, all of it visible in the callbacks rather
 * than in this decision:
 *
 *  - `onContentSizeChange` re-framed ANIMATED, every time, so the opening
 *    layout of a long transcript was a visible glide rather than a landing —
 *    and in voice mode, where the interim-transcript bubble grows on every STT
 *    partial (the screen says so at `onText: setVoiceInterim`), an animated
 *    scroll was started per partial, each one interrupting the last.
 *  - Nothing consulted the reader. A reader scrolled up to re-read an earlier
 *    turn was pulled back to the end by any growth — including the keyboard
 *    opening, which re-lays the list out without adding a single message.
 *  - The card branch had the mirror of that: every content-size change pinned
 *    back to the top, so a long scenario card could not be read past its fold.
 *
 * It lives in this module rather than beside the screen for the reason the
 * repository's node tests keep running into: a module loaded under `node
 * --test` must import nothing, so the alternative to sharing this file was a
 * second copy of the follow rule. One place decides where a message list
 * sits.
 */

/** What the list should be showing. `none` means leave the reader alone. */
export type PracticeFocusTarget = "summary" | "end" | "top" | "none";

/** An explicit ask, naming what the user just did — or `false` for a layout. */
export type PracticeFocusForce = false | "end" | "top";

export type PracticeFocusPlan = {
  target: PracticeFocusTarget;
  /** Never animated for a landing; only for following a live conversation. */
  animated: boolean;
};

const STAY: PracticeFocusPlan = { target: "none", animated: false };

export function practiceFocusPlan(input: {
  /**
   * The user did something that means "put me where the new thing is", and
   * WHAT they did — sending a line asks for the end, swapping the scene asks
   * for the top of the new card. `false` for the list's own layout callbacks.
   *
   * An explicit ask outranks both the settle window and the reader's position,
   * because the reader IS the one asking, and it is always instant: a position,
   * not a journey. It names its own target rather than reading
   * `hasConversation`, because the caller acts on a state React has not
   * rendered yet — the first line of a session is sent while the screen still
   * believes there is no conversation.
   */
  forced: PracticeFocusForce;
  /** Has the content height stopped moving? While false, every pass re-pins. */
  settled: boolean;
  /** Is the reader at the bottom (`isFollowing`)? */
  following: boolean;
  /** Roleplay turns or BYO intake on screen, as opposed to just a card. */
  hasConversation: boolean;
  /** Is the end-of-session feedback in the list? */
  hasSummary: boolean;
  /** Has that feedback already been framed once? */
  summaryFramed: boolean;
}): PracticeFocusPlan {
  if (input.hasSummary) {
    // Frame the feedback when it arrives, then never again: the session is
    // over, there is nothing to follow, and re-asserting on every later layout
    // change would drag the reader back to the top each time they scrolled
    // down to the buttons under it.
    if (input.summaryFramed) return STAY;
    return { target: "summary", animated: input.settled };
  }

  if (input.forced) return { target: input.forced, animated: false };

  if (!input.hasConversation) {
    // A card is read from the top down. Pin there while it is still laying out
    // and then leave it alone, so a card that overflows the screen can be read
    // past its fold. A replacement card is framed by the ask above.
    if (!input.settled) return { target: "top", animated: false };
    return STAY;
  }

  const action = followAction({
    settled: input.settled,
    following: input.following,
    hasMessages: true,
  });
  if (action === "stay") return STAY;
  // Never animated, for the reason `use-thread-follow.ts` sets out at length:
  // an animated scroll toward a moving end cannot be confirmed, so it lands
  // short and stays there. The summary above is different — it is framed once,
  // at a fixed index, and nothing grows underneath it.
  return { target: "end", animated: false };
}

// ------------------------------------------------------------------ inverted

/**
 * The follow rule for an INVERTED thread, where the newest message lives at
 * offset zero.
 *
 * Every thread renders newest-first inside an `inverted` list once it holds a
 * conversation (the practice transcript flips when its first line is sent),
 * and that changes what "the end" is. In a normal list the
 * end is `contentHeight - viewportHeight` — a value that moves every time a
 * row lands, which is what the whole confirm-loop above exists to chase. In an
 * inverted list the newest message sits at offset 0, and 0 does not move:
 * content growth happens at the far end of the scroll range, away from the
 * reader. Verified against a real react-native-web@0.21.2 `FlatList` in
 * Chromium: the first painted frame of an inverted thread already shows the
 * newest message, an arrival while scrolled up holds the reader's row to the
 * pixel, and a page of history appended behind does the same.
 *
 * So the inverted rule needs only the offset. No viewport height, no content
 * height, no settle window, no confirmation — those were all compensations
 * for aiming at a moving target.
 */
export function isFollowingInverted(offsetY: number): boolean {
  // The same slop as `FOLLOW_THRESHOLD_PX`, for the same reason: a reader a
  // few pixels off the newest message after a keyboard opens or a row
  // re-measures has not chosen to leave it. Offsets past zero from iOS
  // rubber-banding land below the threshold and stay "following", correctly.
  return offsetY <= FOLLOW_THRESHOLD_PX;
}
